import React, { useCallback, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import { cn } from 'lib/utils';
import UntappedCarrierSections from './UntappedCarrierSections';
import UntappedStatesModal from './UntappedStatesModal';
import UntappedSuggestSummaryModal from './UntappedSuggestSummaryModal';
import { suggestUntappedAgentRecommendations } from './agencyRecommendationTracking';
import { groupAvailableCarriersByParent } from './untappedAgentsHelpers';

export default function UntappedAgentCard({
  agencyId,
  agencyName,
  mainZohoId,
  agent,
  availableCarriers,
  onSuggest,
  defaultExpanded = false
}) {
  const { agentName, agentId, email: agentEmail, npn } = agent;
  const hasAgentEmail = Boolean(agentEmail);
  const hasCarriers = availableCarriers.length > 0;
  const carrierCount = availableCarriers.length;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [sending, setSending] = useState(false);

  const groupedCarriers = useMemo(
    () => groupAvailableCarriersByParent(availableCarriers),
    [availableCarriers]
  );

  const [statesModal, setStatesModal] = useState({
    open: false,
    carrierName: '',
    states: []
  });
  const [suggestModalOpen, setSuggestModalOpen] = useState(false);

  const handleSeeStates = useCallback((carrierName, states) => {
    setStatesModal({ open: true, carrierName, states });
  }, []);

  const closeStatesModal = useCallback(() => {
    setStatesModal({ open: false, carrierName: '', states: [] });
  }, []);

  const openSuggestModal = useCallback(() => {
    if (!hasCarriers || sending) return;
    setSuggestModalOpen(true);
  }, [hasCarriers, sending]);

  const closeSuggestModal = useCallback(() => {
    if (sending) return;
    setSuggestModalOpen(false);
  }, [sending]);

  const handleConfirmSuggest = useCallback(async () => {
    if (sending || !hasCarriers) return;

    setSending(true);
    try {
      const sent = await suggestUntappedAgentRecommendations({
        agencyId,
        agencyName,
        mainZohoId,
        agentId,
        agentName,
        email: agentEmail,
        npn,
        availableCarriers
      });

      if (sent) {
        onSuggest?.({ agentId, agentName, agencyId, availableCarriers });
        setSuggestModalOpen(false);
      }
    } finally {
      setSending(false);
    }
  }, [
    sending,
    hasCarriers,
    agencyId,
    agencyName,
    mainZohoId,
    agentId,
    agentName,
    agentEmail,
    npn,
    availableCarriers,
    onSuggest
  ]);

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const suggestionLabel =
    carrierCount === 1
      ? '1 carrier recommendation'
      : `${carrierCount} carrier recommendations`;

  return (
    <article
      className={cn(
        'flex w-full min-w-0 flex-col rounded-[10px]',
        'border border-grey-lighter bg-white'
      )}
    >
      <div className='flex flex-wrap items-start gap-3 p-4'>
        <button
          type='button'
          className={cn(
            'flex min-w-0 flex-1 items-start gap-2 border-none bg-transparent p-0 text-left',
            'cursor-pointer rounded-md transition-colors hover:bg-grey-background/60'
          )}
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={`untapped-suggestions-${agentId}`}
        >
          <div className='min-w-0 flex-1'>
            <h4 className='m-0 text-lg font-semibold text-primary-darkened'>
              {agentName}
            </h4>
            {agencyName && (
              <p className='m-0 mt-0.5 truncate text-xs text-grey'>
                {agencyName}
              </p>
            )}
            <p className='m-0 mt-1 text-xs font-medium text-grey'>
              {hasCarriers
                ? suggestionLabel
                : 'No matching carrier recommendations'}
            </p>
            {hasAgentEmail ? (
              <p className='m-0 mt-1 text-xs text-grey'>
                Email:{' '}
                <span className='font-medium text-primary-darkened'>
                  {agentEmail}
                </span>
              </p>
            ) : (
              <p className='m-0 mt-1 text-xs font-medium text-semantic-danger'>
                No email on file
              </p>
            )}
          </div>
          <ChevronDown
            className={cn(
              'mt-1 size-5 shrink-0 text-grey transition-transform duration-200',
              expanded && 'rotate-180'
            )}
            aria-hidden
          />
        </button>

        <Button
          variant='primary'
          size='small'
          text='Suggest to agent'
          className='shrink-0'
          isDisabled={!hasCarriers || sending}
          isLoading={sending}
          onClick={openSuggestModal}
        />
      </div>

      {expanded && (
        <div
          id={`untapped-suggestions-${agentId}`}
          className='flex flex-col gap-4 border-t border-grey-lighter px-4 pb-4 pt-3'
        >
          {hasCarriers ? (
            <UntappedCarrierSections
              groupedCarriers={groupedCarriers}
              onSeeStates={handleSeeStates}
            />
          ) : (
            <p className='m-0 text-sm text-grey'>
              No carrier recommendations are available for this agent.
            </p>
          )}
        </div>
      )}

      <UntappedSuggestSummaryModal
        active={suggestModalOpen}
        agentName={agentName}
        agentEmail={agentEmail}
        groupedCarriers={groupedCarriers}
        confirmLoading={sending}
        onClose={closeSuggestModal}
        onConfirm={handleConfirmSuggest}
      />

      <UntappedStatesModal
        active={statesModal.open}
        carrierName={statesModal.carrierName}
        states={statesModal.states}
        onClose={closeStatesModal}
      />
    </article>
  );
}

UntappedAgentCard.propTypes = {
  agencyId: propTypes.string,
  agencyName: propTypes.string,
  mainZohoId: propTypes.string,
  agent: propTypes.shape({
    agentId: propTypes.string.isRequired,
    agentName: propTypes.string.isRequired,
    email: propTypes.string,
    npn: propTypes.string
  }).isRequired,
  availableCarriers: propTypes.arrayOf(
    propTypes.shape({
      id: propTypes.string,
      name: propTypes.string.isRequired,
      considersStates: propTypes.bool,
      states: propTypes.arrayOf(propTypes.string)
    })
  ).isRequired,
  onSuggest: propTypes.func,
  defaultExpanded: propTypes.bool
};
